import { isEqual } from 'lodash/fp';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { TableState } from 'dashboard/lib/interfaces/TableState';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import {
  WorkflowRunsActions,
  WorkflowRunsActionType,
  WorkflowRunsFilterParams,
} from 'dashboard/redux/actions/workflow-runs';

export type WorkflowRuns = Record<string, WorkflowRun[]>;

export type ManualRunResults = {
  validObjectIds?: string[];
  invalidObjectIds?: string[];
};

export interface WorkflowRunsState extends TableState<WorkflowRunsFilterParams> {
  workflowRuns: WorkflowRuns;
  workflows: Record<string, Workflow>;
  workflowVersions: Record<string, WorkflowVersion>;
  manualRunResults?: ManualRunResults;
}

const initialState: WorkflowRunsState = {
  workflowRuns: {},
  workflows: {},
  workflowVersions: {},
  paginationParams: undefined,
  filters: {
    completedAtStart: undefined,
    completedAtEnd: undefined,
    status: [],
    workflowIds: [],
    workflowVersionIds: [],
    omni: undefined,
  },
  sortParams: {
    sort: 'created_at',
    shouldSortDescending: true,
  },
  pageSize: DEFAULT_PAGE_SIZE,
  currentPage: 1,
  manualRunResults: undefined,
};

export default (state: WorkflowRunsState = initialState, action: WorkflowRunsActions) => {
  switch (action.type) {
    case WorkflowRunsActionType.SetWorkflowRuns: {
      return {
        ...state,
        workflowRuns: {
          ...state.workflowRuns,
          [action.payload.environment.id]: action.payload.workflowRuns,
        },
        paginationParams: action.payload.paginationParams,
      };
    }

    case WorkflowRunsActionType.SetWorkflows: {
      return {
        ...state,
        workflows: {
          ...action.payload.workflows,
        },
      };
    }

    case WorkflowRunsActionType.SetWorkflowVersions: {
      return {
        ...state,
        workflowVersions: {
          ...action.payload.workflowVersions,
        },
      };
    }

    case WorkflowRunsActionType.SetWorkflowRunFilters: {
      const newFilters: WorkflowRunsFilterParams = {
        ...state.filters,
        ...action.payload.filters,
      };

      if (isEqual(newFilters, state.filters)) {
        return state;
      } else {
        return {
          ...state,
          filters: newFilters,
        };
      }
    }

    case WorkflowRunsActionType.SetWorkflowRunSortParams: {
      return {
        ...state,
        sortParams: {
          ...action.payload.sortParams,
        },
      };
    }

    case WorkflowRunsActionType.SetPageSize: {
      return {
        ...state,
        pageSize: action.payload.pageSize,
      };
    }

    case WorkflowRunsActionType.SetCurrentPage: {
      return {
        ...state,
        currentPage: action.payload.currentPage,
      };
    }

    case WorkflowRunsActionType.SetManualRunResults: {
      return {
        ...state,
        manualRunResults: {
          validObjectIds: action.payload.validObjectIds,
          invalidObjectIds: action.payload.invalidObjectIds,
        },
      };
    }

    case WorkflowRunsActionType.ClearManualRunResults: {
      return {
        ...state,
        manualRunResults: undefined,
      };
    }

    default:
      return state;
  }
};
